/**
 * useForceSimulation
 *
 * D3 force simulation hook for network graph
 * - Manages node/link positions
 * - Auto-stops when stable
 * - Supports restart for drag
 */

import { useRef, useEffect, useState, useCallback } from 'react';
import * as d3 from 'd3';

const NODE_RADIUS = 24;

/**
 * @param {Array} nodes - 노드 배열
 * @param {Array} links - 링크 배열
 * @param {Object} options - { width, height, minWeight }
 * @returns {{ nodes, links, simulation, restart, stop }}
 */
export const useForceSimulation = (nodes, links, options = {}) => {
  const { width = 800, height = 600, minWeight = 20 } = options;

  const simulationRef = useRef(null);
  const [simulatedNodes, setSimulatedNodes] = useState([]);
  const [simulatedLinks, setSimulatedLinks] = useState([]);
  const [isStable, setIsStable] = useState(false);

  // 링크 필터링
  const filteredLinks = links.filter(l => l.weight >= minWeight);

  useEffect(() => {
    if (!nodes.length) return;

    // 노드 복사 (D3가 직접 수정하므로)
    const nodesCopy = nodes.map(d => ({ ...d }));

    // 링크 복사 (source/target를 객체 참조로 변환됨)
    const linksCopy = filteredLinks.map(d => ({ ...d }));

    // Force simulation 생성
    const simulation = d3.forceSimulation(nodesCopy)
      .force('link', d3.forceLink(linksCopy)
        .id(d => d.id)
        .distance(80)
        .strength(d => Math.min(d.weight / 500, 0.5))
      )
      .force('charge', d3.forceManyBody()
        .strength(-100)
        .distanceMax(400)
      )
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('collision', d3.forceCollide(NODE_RADIUS + 4))
      .alphaDecay(0.02)
      .velocityDecay(0.4);

    simulationRef.current = simulation;

    // 초기 레이아웃 계산 (blocking)
    simulation.tick(150);
    setSimulatedNodes([...nodesCopy]);
    setSimulatedLinks([...linksCopy]);

    // 틱 핸들러
    simulation.on('tick', () => {
      setSimulatedNodes([...nodesCopy]);
      setSimulatedLinks([...linksCopy]);

      if (simulation.alpha() < 0.01) {
        setIsStable(true);
      }
    });

    simulation.on('end', () => {
      setIsStable(true);
    });

    return () => {
      simulation.stop();
    };
  }, [nodes, filteredLinks, width, height]);

  // 시뮬레이션 재시작 (드래그용)
  const restart = useCallback((alpha = 0.3) => {
    if (simulationRef.current) {
      setIsStable(false);
      simulationRef.current.alphaTarget(alpha).restart();
    }
  }, []);

  // 시뮬레이션 정지
  const stop = useCallback(() => {
    if (simulationRef.current) {
      simulationRef.current.alphaTarget(0);
    }
  }, []);

  // 노드 위치 고정/해제 (드래그용)
  const fixNode = useCallback((node, x, y) => {
    node.fx = x;
    node.fy = y;
  }, []);

  const releaseNode = useCallback((node) => {
    node.fx = null;
    node.fy = null;
  }, []);

  return {
    nodes: simulatedNodes,
    links: simulatedLinks,
    simulation: simulationRef.current,
    isStable,
    restart,
    stop,
    fixNode,
    releaseNode,
  };
};

export default useForceSimulation;
